import React from "react";
import { GenderButton } from "../poids-ideal/App";
import { UNITS, ckdEpi2009, cockcroftGault, mdrd, stageFor, toMgDl, validate } from "./clairanceRules";

const fr = (n) => String(Math.round(n)).replace(".", ",");
const num = (s) => parseFloat(String(s).replace(",", "."));

const inputCls = "py-2 w-20 sm:w-24 text-center px-2 font-bold rounded-lg text-base sm:text-lg focus:outline-none focus:ring focus:ring-slate-300";

function Field({ id, label, optional, value, onChange, unit, decimal }) {
    return (
        <div>
            <label htmlFor={id} className="text-sm sm:text-base mb-2 block cursor-default">
                {label} {optional && <span className="font-normal text-gray-500">(optionnel)</span>}
            </label>
            <div className="flex items-center gap-2">
                <input id={id} type="text" inputMode={decimal ? "decimal" : "numeric"} placeholder="—" className={inputCls}
                    value={value} onChange={(e) => onChange(e.target.value.replace(decimal ? /[^0-9.,]/g : /[^0-9]/g, ""))} />
                {unit}
            </div>
        </div>
    );
}

function FormulaRow({ name, note, value, unit }) {
    return (
        <div className="flex items-center justify-between bg-stone-100 rounded-lg px-4 py-2">
            <div><div className="font-semibold text-sm sm:text-base text-gray-900">{name}</div><div className="text-xs text-gray-600">{note}</div></div>
            <div className="font-bold text-base sm:text-lg text-emerald-800 text-right shrink-0 ml-2">{value}{unit && <span className="block text-xs font-normal text-gray-600">{unit}</span>}</div>
        </div>
    );
}

export default function Clairance() {
    const [sex, setSex] = React.useState("male");
    const [age, setAge] = React.useState("");
    const [creat, setCreat] = React.useState("");
    const [unit, setUnit] = React.useState("umol");
    const [weight, setWeight] = React.useState("");

    const a = num(age);
    const scr = creat ? toMgDl(num(creat), unit) : NaN;
    const w = num(weight);
    const ok = validate({ age: a, scr, weight: w });
    const ready = ok.age && ok.scr;
    const p = { age: a, sex, scr, weight: w };
    const ckd = ready ? ckdEpi2009(p) : null;
    // Staged on the value shown (rounded, as labs report it): staging 59.6 as G3a
    // next to a displayed "60" would contradict the page's own table.
    const stage = ckd !== null ? stageFor(Math.round(ckd)) : null;

    return (
        <>
            <div className="flex w-full text-center gap-2 sm:gap-4">
                <GenderButton gender="male" selected={sex === "male"} onClick={() => setSex("male")} />
                <GenderButton gender="female" selected={sex === "female"} onClick={() => setSex("female")} />
            </div>

            <div className="p-4 my-2 sm:my-4 bg-stone-100 rounded-lg flex flex-wrap gap-6">
                <Field id="cl-age" label="Âge" value={age} onChange={setAge} unit={<span className="text-base sm:text-xl font-bold cursor-default">ans</span>} />
                <Field id="cl-creat" label="Créatininémie" value={creat} onChange={setCreat} decimal
                    unit={
                        <select aria-label="Unité de la créatinine" className="dropdown-filter select" value={unit} onChange={(e) => setUnit(e.target.value)}>
                            {Object.entries(UNITS).map(([k, u]) => <option key={k} value={k}>{u.label}</option>)}
                        </select>
                    } />
                <Field id="cl-weight" label="Poids" optional value={weight} onChange={setWeight} decimal unit={<span className="text-base sm:text-xl font-bold cursor-default">kg</span>} />
            </div>

            {age && !ok.age && <p className="text-sm text-red-700 text-center mb-2">Ces formules concernent les adultes de 18 ans et plus.</p>}
            {creat && !ok.scr && <p className="text-sm text-red-700 text-center mb-2">Valeur de créatinine inhabituelle : vérifiez l'unité ({UNITS[unit].label}).</p>}

            {!ready && (
                <div className="p-6 my-2 sm:my-4 bg-stone-50 border border-dashed border-stone-300 rounded-lg text-center">
                    <p className="text-gray-900 font-semibold text-sm sm:text-base">Renseignez l'âge et la créatininémie</p>
                    <p className="text-gray-600 text-sm mt-2">Vous obtiendrez le DFG estimé (CKD-EPI et MDRD), le stade de la maladie rénale chronique et, avec le poids, la clairance de Cockcroft-Gault.</p>
                </div>
            )}

            {ready && (
                <>
                    <div className="p-4 my-2 sm:my-4 rounded-lg w-full bg-gradient-to-b from-emerald-900 to-emerald-700 text-white text-center">
                        <div className="text-sm text-emerald-200">DFG estimé (CKD-EPI 2009)</div>
                        <div className="text-4xl sm:text-5xl font-bold my-1">{fr(ckd)} <span className="text-base font-semibold">mL/min/1,73 m²</span></div>
                        <div className="text-sm font-semibold">Stade {stage.code} · {stage.label}</div>
                        {(stage.code === "G1" || stage.code === "G2") && (
                            <div className="text-xs text-emerald-100 mt-1">Maladie rénale chronique seulement si d'autres marqueurs d'atteinte rénale persistent plus de 3 mois.</div>
                        )}
                    </div>

                    <div className="mb-2 font-bold text-center w-full sm:text-lg cursor-default">Selon les différentes formules</div>
                    <div className="flex flex-col gap-2">
                        <FormulaRow name="CKD-EPI 2009" note="Référence en France (HAS)" value={fr(ckd)} unit="mL/min/1,73 m²" />
                        <FormulaRow name="MDRD" note="Moins précise au-dessus de 60" value={fr(mdrd(p))} unit="mL/min/1,73 m²" />
                        <FormulaRow
                            name="Cockcroft-Gault"
                            note={ok.weight ? "Clairance, pour adapter les posologies" : "Renseignez le poids"}
                            value={ok.weight ? fr(cockcroftGault(p)) : "—"}
                            unit={ok.weight ? "mL/min" : null}
                        />
                    </div>
                    <p className="text-xs text-gray-600 mt-3">
                        Estimations à interpréter par un professionnel de santé, selon le contexte clinique. Elles ne sont pas valables chez l'enfant, la femme enceinte, ni en cas de variation rapide de la fonction rénale ou de masse musculaire très inhabituelle.
                    </p>
                </>
            )}
        </>
    );
}
